<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'send', 'selectMedia']);
const { t } = useI18n();

const messageType = ref('buttons');
const mediaInput = ref(null);
const mediaKind = ref('');
const mediaPtt = ref(false);
const mediaPtv = ref(false);
const mediaViewOnce = ref(false);
const mediaGifPlayback = ref(false);
const forwarded = ref(false);
const forwardingScore = ref(1);
const mentionedJids = ref('');
const promotional = ref(false);

const pollName = ref('');
const pollOptions = ref(['', '']);
const selectableOptionsCount = ref(1);
const buttonText = ref('');
const buttonHeader = ref('');
const buttonFooter = ref('');
const buttonItems = ref([
  { id: '', title: '' },
  { id: '', title: '' },
]);
const listTitle = ref('');
const listText = ref('');
const listButtonText = ref('');
const listFooter = ref('');
const listSections = ref([
  { title: '', rows: [{ id: '', title: '', description: '' }] },
]);
const latitude = ref('');
const longitude = ref('');
const locationName = ref('');
const locationAddress = ref('');
const locationUrl = ref('');
const liveCaption = ref('');
const liveAccuracy = ref('');
const liveSpeed = ref('');
const liveHeading = ref('');
const liveTimeOffset = ref('');
const liveSequenceNumber = ref('');
const contactName = ref('');
const contactPhone = ref('');
const contacts = ref([
  { displayName: '', phone: '' },
  { displayName: '', phone: '' },
]);

const maxPollOptions = 12;
const maxButtons = 3;
const maxListRows = 10;
const maxContacts = 100;

const localShow = computed({
  get: () => props.show,
  set: value => {
    if (!value) emit('close');
  },
});

const messageTypes = computed(() => [
  {
    value: 'buttons',
    icon: 'i-ph-hand-tap',
    label: t('WHATSAPP_MESSAGE.BUTTONS'),
  },
  {
    value: 'list',
    icon: 'i-ph-list-bullets',
    label: t('WHATSAPP_MESSAGE.LIST'),
  },
  { value: 'poll', icon: 'i-ph-chart-bar', label: t('WHATSAPP_MESSAGE.POLL') },
  {
    value: 'location',
    icon: 'i-ph-map-pin',
    label: t('WHATSAPP_MESSAGE.LOCATION'),
  },
  {
    value: 'live_location',
    icon: 'i-ph-navigation-arrow',
    label: t('WHATSAPP_MESSAGE.LIVE_LOCATION'),
  },
  {
    value: 'contact',
    icon: 'i-ph-user-circle-plus',
    label: t('WHATSAPP_MESSAGE.CONTACT'),
  },
  {
    value: 'contacts',
    icon: 'i-ph-users-three',
    label: t('WHATSAPP_MESSAGE.CONTACTS'),
  },
]);

const mediaActions = computed(() => [
  { kind: 'image', icon: 'i-ph-image', label: t('WHATSAPP_MESSAGE.PHOTO') },
  {
    kind: 'video',
    icon: 'i-ph-video-camera',
    label: t('WHATSAPP_MESSAGE.VIDEO'),
  },
  {
    kind: 'audio',
    icon: 'i-ph-music-notes',
    label: t('WHATSAPP_MESSAGE.AUDIO'),
  },
  {
    kind: 'document',
    icon: 'i-ph-file',
    label: t('WHATSAPP_MESSAGE.DOCUMENT'),
  },
  {
    kind: 'sticker',
    icon: 'i-ph-smiley-sticker',
    label: t('WHATSAPP_MESSAGE.STICKER'),
  },
]);

const mediaAccept = computed(() => {
  if (mediaKind.value === 'sticker') return 'image/webp';
  if (mediaKind.value === 'image') return 'image/*';
  if (mediaKind.value === 'video') return 'video/*';
  if (mediaKind.value === 'audio') return 'audio/*';
  return '*/*';
});

const normalizedPollOptions = computed(() =>
  pollOptions.value.map(option => option.trim()).filter(Boolean)
);
const normalizedButtons = computed(() =>
  buttonItems.value
    .map(item => ({ id: item.id.trim(), title: item.title.trim() }))
    .filter(item => item.id && item.title)
);
const normalizedSections = computed(() =>
  listSections.value
    .map(section => ({
      title: section.title.trim(),
      rows: section.rows
        .map(row => ({
          id: row.id.trim(),
          title: row.title.trim(),
          description: row.description.trim(),
        }))
        .filter(row => row.id && row.title),
    }))
    .filter(section => section.rows.length)
);
const normalizedContacts = computed(() =>
  contacts.value
    .map(contact => ({
      display_name: contact.displayName.trim(),
      phone: contact.phone.replace(/\D/g, ''),
    }))
    .filter(contact => contact.display_name && contact.phone)
);
const normalizedMentionedJids = computed(() =>
  mentionedJids.value
    .split(/[\s,;]+/)
    .map(jid => jid.trim())
    .filter(Boolean)
    .slice(0, 100)
);
const isValidCoordinates = computed(
  () =>
    latitude.value !== '' &&
    longitude.value !== '' &&
    Number.isFinite(Number(latitude.value)) &&
    Number(latitude.value) >= -90 &&
    Number(latitude.value) <= 90 &&
    Number.isFinite(Number(longitude.value)) &&
    Number(longitude.value) >= -180 &&
    Number(longitude.value) <= 180
);
const validPoll = computed(() => {
  const options = normalizedPollOptions.value;
  return (
    pollName.value.trim() &&
    options.length >= 2 &&
    options.length <= maxPollOptions &&
    new Set(options).size === options.length
  );
});
const validButtons = computed(() => {
  const buttons = normalizedButtons.value;
  return (
    buttonText.value.trim() &&
    buttons.length >= 1 &&
    buttons.length <= maxButtons &&
    new Set(buttons.map(button => button.id)).size === buttons.length
  );
});
const validList = computed(() => {
  const rows = normalizedSections.value.flatMap(section => section.rows);
  return (
    listText.value.trim() &&
    listButtonText.value.trim() &&
    rows.length >= 1 &&
    rows.length <= maxListRows &&
    new Set(rows.map(row => row.id)).size === rows.length
  );
});
const validContact = computed(() => {
  const digits = contactPhone.value.replace(/\D/g, '');
  return contactName.value.trim() && digits.length >= 10 && digits.length <= 15;
});
const validContacts = computed(
  () =>
    normalizedContacts.value.length >= 1 &&
    normalizedContacts.value.length <= maxContacts &&
    normalizedContacts.value.every(
      contact => contact.phone.length >= 10 && contact.phone.length <= 15
    )
);
const isValid = computed(
  () =>
    ({
      buttons: validButtons.value,
      list: validList.value,
      poll: validPoll.value,
      location: isValidCoordinates.value,
      live_location: isValidCoordinates.value,
      contact: validContact.value,
      contacts: validContacts.value,
    })[messageType.value]
);

const totalListRows = computed(() =>
  listSections.value.reduce((total, section) => total + section.rows.length, 0)
);

const addPollOption = () => {
  if (pollOptions.value.length < maxPollOptions) pollOptions.value.push('');
};
const removePollOption = index => {
  if (pollOptions.value.length > 2) pollOptions.value.splice(index, 1);
};
const addButton = () => {
  if (buttonItems.value.length < maxButtons)
    buttonItems.value.push({ id: '', title: '' });
};
const removeButton = index => {
  if (buttonItems.value.length > 1) buttonItems.value.splice(index, 1);
};
const addSection = () =>
  listSections.value.push({
    title: '',
    rows: [{ id: '', title: '', description: '' }],
  });
const removeSection = index => {
  if (listSections.value.length > 1) listSections.value.splice(index, 1);
};
const addListRow = sectionIndex => {
  if (totalListRows.value < maxListRows) {
    listSections.value[sectionIndex].rows.push({
      id: '',
      title: '',
      description: '',
    });
  }
};
const removeListRow = (sectionIndex, rowIndex) => {
  const rows = listSections.value[sectionIndex].rows;
  if (rows.length > 1) rows.splice(rowIndex, 1);
};
const addContact = () => {
  if (contacts.value.length < maxContacts)
    contacts.value.push({ displayName: '', phone: '' });
};
const removeContact = index => {
  if (contacts.value.length > 1) contacts.value.splice(index, 1);
};

const selectMedia = kind => {
  mediaKind.value = kind;
  mediaPtt.value = false;
  mediaPtv.value = false;
  mediaViewOnce.value = false;
  mediaGifPlayback.value = false;
};

const openMediaPicker = () => {
  mediaInput.value?.click();
};

function messageContext() {
  return {
    forwarded: forwarded.value,
    ...(forwarded.value
      ? {
          forwarding_score: Math.min(
            Math.max(Number(forwardingScore.value) || 1, 1),
            5
          ),
        }
      : {}),
    ...(normalizedMentionedJids.value.length
      ? { mentioned_jids: normalizedMentionedJids.value }
      : {}),
    ...(promotional.value ? { promotion: { promotional: true } } : {}),
  };
}

const onMediaSelected = event => {
  const [file] = event.target.files || [];
  if (file) {
    emit('selectMedia', {
      file,
      kind: mediaKind.value,
      options: {
        ptt: mediaKind.value === 'audio' && mediaPtt.value,
        ptv: mediaKind.value === 'video' && mediaPtv.value,
        viewOnce:
          ['image', 'video'].includes(mediaKind.value) && mediaViewOnce.value,
        gifPlayback:
          mediaKind.value === 'video' &&
          !mediaPtv.value &&
          mediaGifPlayback.value,
      },
      context: messageContext(),
    });
  }
  event.target.value = '';
};

const payloadFor = () => {
  if (messageType.value === 'buttons') {
    return {
      message: buttonText.value.trim(),
      payload: {
        text: buttonText.value.trim(),
        header: buttonHeader.value.trim(),
        footer: buttonFooter.value.trim(),
        buttons: normalizedButtons.value,
      },
    };
  }

  if (messageType.value === 'list') {
    return {
      message: listText.value.trim(),
      payload: {
        title: listTitle.value.trim(),
        text: listText.value.trim(),
        button_text: listButtonText.value.trim(),
        footer: listFooter.value.trim(),
        sections: normalizedSections.value,
      },
    };
  }

  if (messageType.value === 'poll') {
    const options = normalizedPollOptions.value;
    return {
      message: `${pollName.value.trim()}\n${options.map(option => `• ${option}`).join('\n')}`,
      payload: {
        name: pollName.value.trim(),
        options,
        selectable_options_count: Math.min(
          Math.max(Number(selectableOptionsCount.value) || 1, 1),
          options.length
        ),
      },
    };
  }

  if (messageType.value === 'location') {
    const title = locationName.value.trim() || t('WHATSAPP_MESSAGE.LOCATION');
    return {
      message: [
        title,
        locationAddress.value.trim(),
        `${latitude.value}, ${longitude.value}`,
      ]
        .filter(Boolean)
        .join('\n'),
      payload: {
        latitude: latitude.value,
        longitude: longitude.value,
        name: locationName.value.trim(),
        address: locationAddress.value.trim(),
        url: locationUrl.value.trim(),
      },
    };
  }

  if (messageType.value === 'live_location') {
    return {
      message: liveCaption.value.trim() || t('WHATSAPP_MESSAGE.LIVE_LOCATION'),
      payload: {
        latitude: latitude.value,
        longitude: longitude.value,
        caption: liveCaption.value.trim(),
        accuracy_in_meters: liveAccuracy.value || undefined,
        speed_mps: liveSpeed.value || undefined,
        heading: liveHeading.value || undefined,
        time_offset: liveTimeOffset.value || undefined,
        sequence_number: liveSequenceNumber.value || undefined,
      },
    };
  }

  if (messageType.value === 'contact') {
    return {
      message: `${contactName.value.trim()}\n${contactPhone.value.trim()}`,
      payload: {
        display_name: contactName.value.trim(),
        phone: contactPhone.value.replace(/\D/g, ''),
      },
    };
  }

  return {
    message: normalizedContacts.value
      .map(contact => `${contact.display_name}\n${contact.phone}`)
      .join('\n\n'),
    payload: { contacts: normalizedContacts.value },
  };
};

const send = () => {
  if (!isValid.value) return;

  const { message, payload } = payloadFor();
  emit('send', {
    message,
    contentAttributes: {
      whatsmeow: {
        message_type: messageType.value,
        payload,
        ...messageContext(),
      },
    },
  });
};

watch(
  () => props.show,
  visible => {
    if (!visible) return;
    forwardingScore.value = 1;
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="() => emit('close')">
    <div class="max-h-[85vh] space-y-5 overflow-y-auto p-5">
      <div>
        <p class="text-lg font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_MESSAGE.TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_MESSAGE.SUBTITLE') }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Button
          v-for="type in messageTypes"
          :key="type.value"
          :label="type.label"
          :icon="type.icon"
          :color="messageType === type.value ? 'brand' : 'slate'"
          :variant="messageType === type.value ? 'solid' : 'faded'"
          justify="start"
          @click="messageType = type.value"
        />
      </div>

      <div class="rounded-lg border border-n-weak p-3">
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('WHATSAPP_MESSAGE.MEDIA_TITLE') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('WHATSAPP_MESSAGE.MEDIA_HINT') }}
        </p>
        <div class="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Button
            v-for="action in mediaActions"
            :key="action.kind"
            :label="action.label"
            :icon="action.icon"
            :color="mediaKind === action.kind ? 'brand' : 'slate'"
            :variant="mediaKind === action.kind ? 'solid' : 'faded'"
            justify="start"
            @click="selectMedia(action.kind)"
          />
        </div>
        <div
          v-if="mediaKind"
          class="mt-3 space-y-3 rounded-lg bg-n-slate-3 p-3 dark:bg-n-slate-4"
        >
          <p class="text-xs font-medium text-n-slate-12">
            {{
              $t('WHATSAPP_MESSAGE.MEDIA_SELECTED', {
                type: mediaActions.find(action => action.kind === mediaKind)
                  ?.label,
              })
            }}
          </p>
          <label
            v-if="mediaKind === 'audio'"
            class="flex items-center gap-2 text-sm text-n-slate-12"
            for="whatsapp-media-ptt"
          >
            <input id="whatsapp-media-ptt" v-model="mediaPtt" type="checkbox" />
            {{ $t('WHATSAPP_MESSAGE.SEND_AS_VOICE_NOTE') }}
          </label>
          <label
            v-if="mediaKind === 'image' || mediaKind === 'video'"
            class="flex items-center gap-2 text-sm text-n-slate-12"
            for="whatsapp-media-view-once"
          >
            <input
              id="whatsapp-media-view-once"
              v-model="mediaViewOnce"
              type="checkbox"
            />
            {{ $t('WHATSAPP_MESSAGE.VIEW_ONCE') }}
          </label>
          <label
            v-if="mediaKind === 'video'"
            class="flex items-center gap-2 text-sm text-n-slate-12"
            for="whatsapp-media-ptv"
          >
            <input id="whatsapp-media-ptv" v-model="mediaPtv" type="checkbox" />
            {{ $t('WHATSAPP_MESSAGE.SEND_AS_VIDEO_NOTE') }}
          </label>
          <label
            v-if="mediaKind === 'video' && !mediaPtv"
            class="flex items-center gap-2 text-sm text-n-slate-12"
            for="whatsapp-media-gif"
          >
            <input
              id="whatsapp-media-gif"
              v-model="mediaGifPlayback"
              type="checkbox"
            />
            {{ $t('WHATSAPP_MESSAGE.GIF_PLAYBACK') }}
          </label>
          <Button
            :label="$t('WHATSAPP_MESSAGE.CHOOSE_FILE')"
            color="slate"
            icon="i-ph-upload-simple"
            size="sm"
            variant="faded"
            @click="openMediaPicker"
          />
        </div>
        <input
          ref="mediaInput"
          class="hidden"
          type="file"
          :accept="mediaAccept"
          @change="onMediaSelected"
        />
      </div>

      <div v-if="messageType === 'buttons'" class="space-y-3">
        <input
          v-model="buttonText"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.BUTTON_BODY')"
        />
        <input
          v-model="buttonHeader"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.HEADER_OPTIONAL')"
        />
        <input
          v-model="buttonFooter"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.FOOTER_OPTIONAL')"
        />
        <div
          v-for="(button, index) in buttonItems"
          :key="index"
          class="flex gap-2"
        >
          <input
            v-model="button.id"
            class="min-w-0 flex-1 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.BUTTON_ID')"
          />
          <input
            v-model="button.title"
            class="min-w-0 flex-1 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.BUTTON_LABEL')"
          />
          <Button
            v-if="buttonItems.length > 1"
            :aria-label="$t('WHATSAPP_MESSAGE.REMOVE')"
            color="ruby"
            icon="i-ph-trash"
            variant="faded"
            @click="removeButton(index)"
          />
        </div>
        <Button
          :label="$t('WHATSAPP_MESSAGE.ADD_BUTTON')"
          :disabled="buttonItems.length >= maxButtons"
          color="slate"
          icon="i-ph-plus"
          size="sm"
          variant="faded"
          @click="addButton"
        />
      </div>

      <div v-else-if="messageType === 'list'" class="space-y-3">
        <input
          v-model="listTitle"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.LIST_TITLE_OPTIONAL')"
        />
        <textarea
          v-model="listText"
          class="min-h-20 w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.LIST_BODY')"
        />
        <input
          v-model="listButtonText"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.LIST_BUTTON')"
        />
        <input
          v-model="listFooter"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.FOOTER_OPTIONAL')"
        />
        <div
          v-for="(section, sectionIndex) in listSections"
          :key="sectionIndex"
          class="space-y-2 rounded-lg border border-n-weak p-3"
        >
          <div class="flex gap-2">
            <input
              v-model="section.title"
              class="min-w-0 flex-1 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.SECTION_TITLE_OPTIONAL')"
            />
            <Button
              v-if="listSections.length > 1"
              :aria-label="$t('WHATSAPP_MESSAGE.REMOVE')"
              color="ruby"
              icon="i-ph-trash"
              variant="faded"
              @click="removeSection(sectionIndex)"
            />
          </div>
          <div
            v-for="(row, rowIndex) in section.rows"
            :key="rowIndex"
            class="grid grid-cols-[1fr_1fr_auto] gap-2"
          >
            <input
              v-model="row.id"
              class="min-w-0 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.ROW_ID')"
            />
            <input
              v-model="row.title"
              class="min-w-0 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.ROW_TITLE')"
            />
            <Button
              v-if="section.rows.length > 1"
              :aria-label="$t('WHATSAPP_MESSAGE.REMOVE')"
              color="ruby"
              icon="i-ph-trash"
              variant="faded"
              @click="removeListRow(sectionIndex, rowIndex)"
            />
            <input
              v-model="row.description"
              class="col-span-2 min-w-0 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.ROW_DESCRIPTION_OPTIONAL')"
            />
          </div>
          <Button
            :label="$t('WHATSAPP_MESSAGE.ADD_ROW')"
            :disabled="totalListRows >= maxListRows"
            color="slate"
            icon="i-ph-plus"
            size="sm"
            variant="faded"
            @click="addListRow(sectionIndex)"
          />
        </div>
        <Button
          :label="$t('WHATSAPP_MESSAGE.ADD_SECTION')"
          color="slate"
          icon="i-ph-plus"
          size="sm"
          variant="faded"
          @click="addSection"
        />
      </div>

      <div v-else-if="messageType === 'poll'" class="space-y-3">
        <input
          v-model="pollName"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.POLL_QUESTION_PLACEHOLDER')"
        />
        <div v-for="(_, index) in pollOptions" :key="index" class="flex gap-2">
          <input
            v-model="pollOptions[index]"
            class="min-w-0 flex-1 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.OPTION', { number: index + 1 })"
          />
          <Button
            v-if="pollOptions.length > 2"
            :aria-label="$t('WHATSAPP_MESSAGE.REMOVE')"
            color="ruby"
            icon="i-ph-trash"
            variant="faded"
            @click="removePollOption(index)"
          />
        </div>
        <div class="flex items-center justify-between gap-3">
          <Button
            :label="$t('WHATSAPP_MESSAGE.ADD_OPTION')"
            :disabled="pollOptions.length >= maxPollOptions"
            color="slate"
            icon="i-ph-plus"
            size="sm"
            variant="faded"
            @click="addPollOption"
          />
          <label
            class="flex items-center gap-2 text-sm text-n-slate-11"
            for="whatsapp-poll-count"
          >
            {{ $t('WHATSAPP_MESSAGE.SELECTABLE_OPTIONS') }}
            <input
              id="whatsapp-poll-count"
              v-model.number="selectableOptionsCount"
              class="w-16 rounded-md border border-n-weak bg-n-background px-2 py-1 text-sm"
              min="1"
              :max="normalizedPollOptions.length || 1"
              type="number"
            />
          </label>
        </div>
      </div>

      <div
        v-else-if="
          messageType === 'location' || messageType === 'live_location'
        "
        class="space-y-3"
      >
        <input
          v-if="messageType === 'location'"
          v-model="locationName"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.LOCATION_NAME_PLACEHOLDER')"
        />
        <div class="grid grid-cols-2 gap-3">
          <input
            v-model="latitude"
            class="rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.LATITUDE')"
            inputmode="decimal"
          />
          <input
            v-model="longitude"
            class="rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.LONGITUDE')"
            inputmode="decimal"
          />
        </div>
        <template v-if="messageType === 'location'">
          <input
            v-model="locationAddress"
            class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.ADDRESS')"
          />
          <input
            v-model="locationUrl"
            class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.URL')"
            type="url"
          />
        </template>
        <template v-else>
          <input
            v-model="liveCaption"
            class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.LIVE_CAPTION_OPTIONAL')"
          />
          <div class="grid grid-cols-2 gap-3">
            <input
              v-model="liveAccuracy"
              class="rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.ACCURACY_OPTIONAL')"
              inputmode="decimal"
              type="number"
            />
            <input
              v-model="liveSpeed"
              class="rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.SPEED_OPTIONAL')"
              inputmode="decimal"
              type="number"
            />
            <input
              v-model="liveHeading"
              class="rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.HEADING_OPTIONAL')"
              inputmode="decimal"
              type="number"
            />
            <input
              v-model="liveTimeOffset"
              class="rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.TIME_OFFSET_OPTIONAL')"
              inputmode="numeric"
              type="number"
            />
            <input
              v-model="liveSequenceNumber"
              class="rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
              :placeholder="$t('WHATSAPP_MESSAGE.SEQUENCE_OPTIONAL')"
              inputmode="numeric"
              type="number"
            />
          </div>
        </template>
      </div>

      <div v-else-if="messageType === 'contact'" class="space-y-3">
        <input
          v-model="contactName"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.CONTACT_NAME')"
        />
        <input
          v-model="contactPhone"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.CONTACT_PHONE')"
          inputmode="tel"
          type="tel"
        />
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="(contact, index) in contacts"
          :key="index"
          class="grid grid-cols-[1fr_1fr_auto] gap-2"
        >
          <input
            v-model="contact.displayName"
            class="min-w-0 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.CONTACT_NAME')"
          />
          <input
            v-model="contact.phone"
            class="min-w-0 rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
            :placeholder="$t('WHATSAPP_MESSAGE.CONTACT_PHONE')"
            inputmode="tel"
            type="tel"
          />
          <Button
            v-if="contacts.length > 1"
            :aria-label="$t('WHATSAPP_MESSAGE.REMOVE')"
            color="ruby"
            icon="i-ph-trash"
            variant="faded"
            @click="removeContact(index)"
          />
        </div>
        <Button
          :label="$t('WHATSAPP_MESSAGE.ADD_CONTACT')"
          :disabled="contacts.length >= maxContacts"
          color="slate"
          icon="i-ph-plus"
          size="sm"
          variant="faded"
          @click="addContact"
        />
      </div>

      <div class="space-y-3 rounded-lg border border-n-weak p-3">
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('WHATSAPP_MESSAGE.CONTEXT_TITLE') }}
        </p>
        <label
          class="flex items-center gap-2 text-sm text-n-slate-12"
          for="whatsapp-forwarded"
        >
          <input id="whatsapp-forwarded" v-model="forwarded" type="checkbox" />
          {{ $t('WHATSAPP_MESSAGE.MARK_FORWARDED') }}
        </label>
        <label
          v-if="forwarded"
          class="flex items-center gap-2 text-sm text-n-slate-11"
          for="whatsapp-forwarding-score"
        >
          {{ $t('WHATSAPP_MESSAGE.FORWARDING_SCORE') }}
          <input
            id="whatsapp-forwarding-score"
            v-model.number="forwardingScore"
            class="w-16 rounded-md border border-n-weak bg-n-background px-2 py-1 text-sm"
            min="1"
            max="5"
            type="number"
          />
        </label>
        <input
          v-model="mentionedJids"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_MESSAGE.MENTIONS_PLACEHOLDER')"
        />
        <label
          class="flex items-center gap-2 text-sm text-n-slate-12"
          for="whatsapp-promotional"
        >
          <input
            id="whatsapp-promotional"
            v-model="promotional"
            type="checkbox"
          />
          {{ $t('WHATSAPP_MESSAGE.MARK_PROMOTIONAL') }}
        </label>
        <p v-if="promotional" class="text-xs text-n-slate-11">
          {{ $t('WHATSAPP_MESSAGE.PROMOTIONAL_NOTICE') }}
        </p>
      </div>

      <div class="flex justify-end gap-2 border-t border-n-weak pt-4">
        <Button
          :label="$t('WHATSAPP_MESSAGE.CANCEL')"
          color="slate"
          variant="faded"
          @click="emit('close')"
        />
        <Button
          :label="$t('WHATSAPP_MESSAGE.SEND')"
          :disabled="!isValid"
          @click="send"
        />
      </div>
    </div>
  </woot-modal>
</template>
